import { useCallback, useMemo } from 'react';
import { LocationFlag } from '@weave/schema-gen-ts/dist/schemas/feature-flags/feature_flags_types.pb';
import { LocationFeatureResponse } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { createStoreWithSubscribe, createShallowStore } from '@frontend/store';
import { FeatureFlagStrategy } from '../types';

export type FeatureFlag = {
  Name: string;
  Value: boolean;
  DisplayName: string;
};

export interface FlagStore {
  featureFlags: Record<string, Map<string, LocationFlag>>;
  setFeatureFlags: (props: { locationId: string; flags: LocationFlag[] }) => void;
  setFeatureFlag: (props: { locationId: string; flag: LocationFlag }) => void;
  customizationFlags: Record<string, Map<Feature | string, LocationFeatureResponse>>;
  /** @deprecated Use `customizationFlagIdEnumMap` instead. Maps flag ID → legacy lowercase name. */
  customizationFlagMap: Record<string, string>;
  /** Maps flag ID → Feature enum value. Preferred over `customizationFlagMap`. */
  customizationFlagIdEnumMap: Record<string, Feature>;
  setCustomizationFlag: (props: { locationId: string; flag: LocationFeatureResponse }) => void;
  setCustomizationFlags: (props: { locationId: string; flags: LocationFeatureResponse[] }) => void;
}

export const useFlagStore = createStoreWithSubscribe<FlagStore>(
  (set) => ({
    featureFlags: {},
    setFeatureFlags: ({ locationId, flags }) =>
      set(
        (state) => {
          if (!state.featureFlags[locationId]) {
            state.featureFlags[locationId] = new Map();
          }

          for (const flag of flags) {
            state.featureFlags[locationId].set(flag.flag?.name ?? '', { ...flag, value: flag.value ?? false });
          }
        },
        false,
        'REGISTER_FEATURE_FLAGS'
      ),
    setFeatureFlag: ({ locationId, flag }) =>
      set(
        (state) => {
          if (!state.featureFlags[locationId]) {
            state.featureFlags[locationId] = new Map();
          }

          state.featureFlags[locationId].set(flag.flag?.name ?? '', { ...flag, value: flag.value ?? false });
        },
        false,
        'REGISTER_FEATURE_FLAG'
      ),
    customizationFlags: {},
    customizationFlagMap: {},
    customizationFlagIdEnumMap: {},
    setCustomizationFlag: ({ locationId, flag }) =>
      set(
        (state) => {
          if (!state.customizationFlags[locationId]) {
            state.customizationFlags[locationId] = new Map();
          }

          if (flag.name && flag.id) {
            // TODO: Milind - Remove legacy name key — use featureEnum only
            const name = flag.name.toLowerCase().replace(/\s+/g, '');
            state.customizationFlags[locationId].set(name, flag);
            state.customizationFlagMap[flag.id] = name;

            if (flag.featureEnum) {
              state.customizationFlags[locationId].set(flag.featureEnum, flag);
              state.customizationFlagIdEnumMap[flag.id] = flag.featureEnum;
            }
          } else {
            console.warn('Flag name is missing', flag);
          }
        },
        false,
        'REGISTER_CUSTOMIZATION_FLAG'
      ),
    setCustomizationFlags: ({ locationId, flags }) =>
      set(
        (state) => {
          if (!state.customizationFlags[locationId]) {
            state.customizationFlags[locationId] = new Map();
          }

          for (const flag of flags) {
            if (flag.name && flag.id) {
              // TODO: Milind - Remove legacy name key — use featureEnum only
              const name = flag.name.toLowerCase().replace(/\s+/g, '');
              state.customizationFlags[locationId].set(name, flag);
              state.customizationFlagMap[flag.id] = name;

              if (flag.featureEnum) {
                state.customizationFlags[locationId].set(flag.featureEnum, flag);
                state.customizationFlagIdEnumMap[flag.id] = flag.featureEnum;
              }
            } else {
              console.warn('Flag name is missing', flag);
            }
          }
        },
        false,
        'REGISTER_CUSTOMIZATION_FLAGS'
      ),
  }),
  { name: 'FlagStore', trace: true }
);

const useFlagShallowStore = createShallowStore(useFlagStore);

export const useAppFlagStoreWithSetters = () => {
  const { setFeatureFlag, setFeatureFlags, setCustomizationFlag, setCustomizationFlags } = useFlagStore.getState();

  const { featureFlags, customizationFlags, customizationFlagMap, customizationFlagIdEnumMap } = useFlagShallowStore(
    'featureFlags',
    'customizationFlags',
    'customizationFlagMap',
    'customizationFlagIdEnumMap'
  );

  return {
    setFeatureFlag,
    setFeatureFlags,
    setCustomizationFlag,
    setCustomizationFlags,
    featureFlags,
    customizationFlags,
    /** @deprecated Use `customizationFlagIdEnumMap` instead. */
    customizationFlagMap,
    customizationFlagIdEnumMap,
  };
};

export const useAppFlagStore = () => {
  const { featureFlags, customizationFlags, customizationFlagMap, customizationFlagIdEnumMap } = useFlagShallowStore(
    'featureFlags',
    'customizationFlags',
    'customizationFlagMap',
    'customizationFlagIdEnumMap'
  );

  const getFeatureFlag = useCallback(
    ({ locationIds, flagName }: { locationIds: string[]; flagName: string }) => {
      return locationIds.map((locationId) => featureFlags[locationId]?.get(flagName));
    },
    [featureFlags]
  );

  const getFeatureFlagLocationMap = useCallback(
    ({ locationIds, flagName }: { locationIds: string[]; flagName: string }) => {
      return locationIds.reduce<Record<string, ReturnType<(typeof featureFlags)[string]['get']>>>((acc, locationId) => {
        const flagValue = featureFlags[locationId]?.get(flagName);
        acc[locationId] = flagValue;
        return acc;
      }, {});
    },
    [featureFlags]
  );

  const getCustomizationFlag = useCallback(
    ({
      locationIds,
      featureEnum,
      flagName,
    }: {
      locationIds: string[];
      /** Preferred. The Feature enum value to look up. */
      featureEnum?: Feature;
      /** @deprecated Use `featureEnum` instead. Legacy lowercase flag name. */
      flagName?: Feature | string;
    }) => {
      const key = featureEnum ?? flagName;
      return locationIds.map((locationId) => (key ? customizationFlags[locationId]?.get(key) : undefined));
    },
    [customizationFlags]
  );

  const getCustomizationFlagLocationMap = useCallback(
    ({
      locationIds,
      featureEnum,
      flagName,
    }: {
      locationIds: string[];
      /** Preferred. The Feature enum value to look up. */
      featureEnum?: Feature;
      /** @deprecated Use `featureEnum` instead. Legacy lowercase flag name. */
      flagName?: Feature | string;
    }) => {
      const key = featureEnum ?? flagName;
      return locationIds.reduce<Record<string, ReturnType<(typeof customizationFlags)[string]['get']>>>(
        (acc, locationId) => {
          const flagValue = key ? customizationFlags[locationId]?.get(key) : undefined;
          acc[locationId] = flagValue;
          return acc;
        },
        {}
      );
    },
    [customizationFlags]
  );

  const getFeatureFlagValue = useCallback(
    ({
      locationIds,
      flagName,
      strategy = 'ANY',
    }: {
      locationIds: string[];
      flagName: string;
      strategy?: FeatureFlagStrategy;
    }) => {
      const list = locationIds.map((locationId) => featureFlags[locationId]?.get(flagName)?.value);

      if (strategy === 'ANY') {
        return list.some(Boolean);
      }

      return list.every(Boolean);
    },
    [featureFlags]
  );

  const getCustomizationFlagValue = useCallback(
    ({
      locationIds,
      featureEnum,
      flagName,
      strategy = 'ANY',
      state = 'ACTIVE',
    }: {
      locationIds: string[];
      /** Preferred. The Feature enum value to look up. */
      featureEnum?: Feature;
      /** @deprecated Use `featureEnum` instead. Legacy lowercase flag name. */
      flagName?: Feature | string;
      strategy?: FeatureFlagStrategy;
      state?: keyof typeof State;
    }) => {
      const key = featureEnum ?? flagName;
      const list = locationIds.map((locationId) => (key ? customizationFlags[locationId]?.get(key)?.state : undefined));

      if (strategy === 'ANY') {
        return list.some((_state) => _state === state);
      }

      return list.every((_state) => _state === state);
    },
    [customizationFlags]
  );

  return useMemo(
    () => ({
      featureFlags,
      customizationFlags,
      /** @deprecated Use `customizationFlagIdEnumMap` instead. */
      customizationFlagMap,
      customizationFlagIdEnumMap,

      getFeatureFlag,
      /**
       * Returns a map of locationIds to their feature flag values for a given feature flag
       */
      getFeatureFlagLocationMap,
      getCustomizationFlag,
      /**
       * Returns a map of locationIds to their customiztion flag values for a given customization flag
       */
      getCustomizationFlagLocationMap,
      getFeatureFlagValue,
      getCustomizationFlagValue,
    }),
    [
      featureFlags,
      customizationFlags,
      customizationFlagMap,
      customizationFlagIdEnumMap,
      getFeatureFlag,
      getFeatureFlagLocationMap,
      getCustomizationFlag,
      getCustomizationFlagLocationMap,
      getFeatureFlagValue,
      getCustomizationFlagValue,
    ]
  );
};
